import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Rest from "../../Generated/RestClient";
import { TenantId } from "../../Services/ArmApi";
import { clientAppSettings } from "../../Models/ClientAppSettings";
import { BlockRowInputLabel } from '../../Views/Shared/BlockRowInputLabel';
import { BlockRowInputField } from '../../Views/Shared/BlockRowInputField';
import { Region, Subscription, WebAppSettings } from '../../Services/ArmApi';

export class BotMigrationDialogState {
    // logic
    bot?: Rest.BotService;
    tenants?: TenantId[];
    selectedTenantId?: string;
    selectedSubscription?: string;
    selectedResourceGroup?: string;
    selectedAppName?: string;
    selectedPricingTier?: string;
    selectedLocation?: string;
    botSettings?: WebAppSettings;
    azureAccessToken?: string;
    subscriptions?: Subscription[];
    locations?: Region[];

    // ui
    isRegistrationBot?: boolean;
    isDesignerBot?: boolean;
    appNameIsValid?: boolean;
    appNameErrMsg?: string;
    isCheckingSubscriptionState?: boolean;
    subscriptionErrMsg?: string;
    migrationIsComplete?: boolean;
    firstCheckboxChecked?: boolean;
    secondCheckboxChecked?: boolean;
    error?: {
        title: string,
        message: string,
        link?: {
            url: string,
            text: string,
        }
    };
}

export class BotMigrationDialogProps {
    botId: string;    
    onCancelClick: () => void;
    onMigrateClick: () => void;
    onChangeAppName: (appName: string) => void;
    onChangeTenant: (tenantId: string) => void;
    onChangeSubscription: (subId: string) => void;
    onChangeLocation: (location: string) => void;
    onChangePricingTier: (tier: string) => void;
    onOpenNewBotClick: () => void;
    onCheckFirstCheckbox: () => void;
    onCheckSecondCheckbox: () => void;
    onSignInClick: () => void;
    onCloseBtnClick: () => void;
}

export class BotMigrationDialog extends React.Component<BotMigrationDialogProps, BotMigrationDialogState> {
    private _subscriptionDropdown: HTMLSelectElement;
    private _subscriptionInput: HTMLInputElement;
    private _resourceGroupInput: HTMLInputElement;
    private _locationDropdown: HTMLSelectElement;
    private _pricingTierInput: HTMLInputElement;
    private _appNameInput: HTMLInputElement;
    private _focusControl: React.ReactInstance;
    private _closeBtn: React.ReactInstance;

    constructor(props) {
        super(props);
        this.state = {};
        this.handleFirstFocusTrapFocusx = this.handleFirstFocusTrapFocusx.bind(this);
        this.handleLastFocusTrapFocus = this.handleLastFocusTrapFocus.bind(this);
        this.handleBotMigrationDialogFocus = this.handleBotMigrationDialogFocus.bind(this);
    }

    private  focusMigrationDialog() {
        const dialogFocusElement = ReactDOM.findDOMNode(this._focusControl) as HTMLElement;
        dialogFocusElement && dialogFocusElement.focus();
    }

    private handleFirstFocusTrapFocusx() {
        this.focusMigrationDialog();
    }

    private handleLastFocusTrapFocus() {
        const dialogFocusElement = ReactDOM.findDOMNode(this._closeBtn) as HTMLElement;
        dialogFocusElement && dialogFocusElement.focus();
    }    

    private handleBotMigrationDialogFocus() {
        const dialogFocusElement = ReactDOM.findDOMNode(this._focusControl) as HTMLTextAreaElement;
        dialogFocusElement && dialogFocusElement.select();
    }

    componentDidMount() {
        this.focusMigrationDialog();
    }

    componentDidUpdate(){
        this.focusMigrationDialog();
    }

    private renderTenantPicker() : JSX.Element[] {
        return this.state.tenants.length > 1 ? [
            <div className={"migration-label-wrapper"} key='tenant-label'>
                <BlockRowInputLabel
                    text={"Directory"}
                    tip={"Select Azure Active Directory"}
                    tipAsHtml={true}
                    required={true} />
            </div>,
            <div className={"migration-tenant-select-wrapper"} key='tenant-select'>
                <select className={"migration-tenant-select"} value={this.state.selectedTenantId} 
                    onChange={ev => this.props.onChangeTenant(ev.target.value)} ref={(ctl) => this._focusControl = ctl} onFocus={this.handleBotMigrationDialogFocus}>
                    { this.state.tenants.map(t => <option key={t.id} value={t.tenantId}>{ t.displayName }</option>) }
                </select>
            </div>
        ]: null;
    }
        /** Renders the form inputs */
    private renderInputs(): JSX.Element {
        const tenantElt = this.renderTenantPicker();
        return this.state.isRegistrationBot ?

            // registration bot
            <div className={"migration-input-container"}>
                <span className={"migration-tenant-header"}>Please select the Azure Directory and the Azure Subscription where you want to migrate your bot to.</span>
                {tenantElt}
                <div className={"migration-label-wrapper"}>
                    <BlockRowInputLabel
                        text={"Azure Subscription"}
                        tip={"The Azure Subscription that will contain your bot."}
                        tipAsHtml={true}
                        required={true} />
                </div>
                <div className={"migration-subs-select-wrapper"}>
                    <select aria-label="Azure Subscription" className={"migration-subs-select"} ref={x => {this._subscriptionDropdown = x; if (tenantElt == null) { this._focusControl = x;}}}  onFocus={this.handleBotMigrationDialogFocus} value={this.state.selectedSubscription} onChange={ev => this.props.onChangeSubscription(ev.target.value)}>
                        { this.state.subscriptions.map(sub => <option key={sub.id} value={sub.subscriptionId}>{ sub.displayName }</option>) }
                    </select>
                    { this.state.subscriptionErrMsg ? <span className={"migration-error"}>{ this.state.subscriptionErrMsg }</span> : null }
                </div>
                <div className={"migration-label-wrapper"}>
                    <BlockRowInputLabel
                        text={"Azure resource group"}
                        tip={"A resource group is a collection of resources that share the same lifecycle, permissions, and policies."}
                        required={true} />
                </div>
                <input className={"migration-input"} ref={x => this._resourceGroupInput = x} value={this.state.bot.Id} readOnly disabled></input>
                <select  aria-label="Azure resource group" className={"migration-input"} ref={x => this._locationDropdown = x} onChange={ev => this.props.onChangeLocation(ev.target.value)} value={this.state.selectedLocation}>
                    { this.state.locations.map(loc => <option key={loc.name} value={loc.name}>{ loc.displayName }</option>) }
                </select>
                <div className={"migration-label-wrapper"}>
                    <BlockRowInputLabel
                        text={"Bot service pricing tier."}
                        tip={`Determines the pricing plan/limits for your bot service. This can be changed later. <a href="https://aka.ms/azure/bots/pricing" target="_blank">Learn more.</a>`}
                        tipAsHtml={true}
                        required={true} />
                </div>
                <input aria-label="Bot service pricing tier" className={"migration-input"} ref={x => this._pricingTierInput = x} value={"S1 Pricing Plan (see tooltip for info)"} readOnly disabled />
            </div> :

            // non-registration bot
            <div className={"migration-input-container"}>
                <span className={"migration-sub-header"}>Destination:</span>
                <div className={"migration-label-wrapper"}>
                    <BlockRowInputLabel
                        text={"Azure app name"}
                        tip={"The name of the new web or function app that will be deployed to Azure. Your bot name will remain the same."}
                        tipAsHtml={true}
                        required={true} />
                </div>
                <div className={"migration-app-name-input-wrapper"}>
                    <input aria-label="Azure app name" className={"migration-app-name-input"} ref={x => this._appNameInput = x} value={this.state.selectedAppName} onChange={ev => this.props.onChangeAppName(ev.target.value)} />
                    { this.state.appNameErrMsg ? <span className={"migration-error"}>{ this.state.appNameErrMsg }</span> : null }
                </div>
                <div className={"migration-label-wrapper"}>
                    <BlockRowInputLabel
                        text={"Azure Subscription"}
                        tip={"The Azure Subscription that will contain your bot."}
                        tipAsHtml={true}
                        required={true} />
                </div>
                <div className={"migration-sub-input-wrapper"}>
                    <input aria-label="Azure Subscription" className={"migration-sub-input"} ref={x => this._subscriptionInput = x} value={this.state.selectedSubscription} readOnly disabled />
                    { this.state.subscriptionErrMsg ? <span className={"migration-error"}>{ this.state.subscriptionErrMsg }</span> : null }
                </div>
                <div className={"migration-label-wrapper"}>
                    <BlockRowInputLabel
                        text={"Azure resource group"}
                        tip={"A resource group is a collection of resources that share the same lifecycle, permissions, and policies."}
                        required={true} />
                </div>
                <input aria-label="Azure resource group" className={"migration-input"} ref={x => this._resourceGroupInput = x} value={this.state.selectedResourceGroup} readOnly disabled />
                <div className={"migration-label-wrapper"}>
                    <BlockRowInputLabel
                    text={"Bot service pricing tier."}
                    tip={`Determines the pricing plan/limits for your bot service. This can be changed later. <a href="https://aka.ms/azure/bots/pricing" target="_blank">Learn more.</a>`}
                    tipAsHtml={true}
                    required={true} />
                </div>
                <input aria-label="Bot service pricing tier" className={"migration-input"} ref={x => this._pricingTierInput = x} value={"S1 Pricing Plan (see tooltip for info)"} readOnly disabled />
            </div>
    }

    /** Renders CTA buttons */
    public renderButtons(focusOnButton: boolean): JSX.Element {

        const cancelButton = focusOnButton ?
                <button className={"migration-cancel m-button-secondary-md"} onClick={() => this.props.onCancelClick()} ref={(ctl) => this._focusControl = ctl} onFocus={this.handleBotMigrationDialogFocus}>Cancel</button>
                :<button className={"migration-cancel m-button-secondary-md"} onClick={() => this.props.onCancelClick()}>Cancel</button>;

        if (this.state.azureAccessToken) {
            const registrationBotCriteria = this.state.selectedSubscription
                && this.state.selectedResourceGroup
                && this.state.selectedLocation
                && this.state.selectedPricingTier
                && !this.state.subscriptionErrMsg
                && !this.state.isCheckingSubscriptionState;

            const nonRegistrationBotCriteria = this.state.appNameIsValid
                && this.state.selectedAppName
                && !this.state.appNameErrMsg
                && !this.state.subscriptionErrMsg
                && !this.state.isCheckingSubscriptionState
                && this.state.selectedSubscription
                && this.state.selectedResourceGroup
                && this.state.selectedPricingTier
                && this.state.botSettings;

            const migrateEnabled = this.state.isRegistrationBot ? registrationBotCriteria : nonRegistrationBotCriteria;            
            return (
                <div className={"migration-button-row"}>
                    {cancelButton}
                    <button className={"migration-migrate m-button-primary-md"} onClick={() => this.props.onMigrateClick()} disabled={!migrateEnabled}>Migrate</button>
                </div>
            );
        } else {
            const signInEnabled = this.state.firstCheckboxChecked && this.state.secondCheckboxChecked;
            return (
                <div className={"migration-button-row"}>
                     {cancelButton} 
                     <button className={"migration-migrate m-button-primary-md"} onClick={() => this.props.onSignInClick()} disabled={!signInEnabled}>Sign in with Azure</button>
                </div>
            );
        }
    }

    /** Renders the prompt for users who are not logged into an Azure account */
    private renderNonAzureAccountView(): JSX.Element {
        return (
            <div className={"migration-dialog-container"}>
                {this.renderFirstFocusTrap()}
                {this.renderCloseButton()}   
                <h1 id="dialog-title" className={"migration-title"}>You need to be signed in with an Azure account</h1>
                <div className={"migration-text"}>We noticed that you are not signed in with an Azure account. Before migrating your bot, you need to take the following steps:</div>
                <div className={"migration-checkbox first-checkbox"}>
                    <label className={"m-checkbox-container"}>
                        <div className={"migration-checkbox-alignment"}>
                            <input className={"m-checkbox"} type="checkbox" ref={(ctl) => this._focusControl = ctl} onFocus={this.handleBotMigrationDialogFocus} onChange={() => this.props.onCheckFirstCheckbox()} />
                            <span className={"m-icon-Checkbox unchecked"}></span>
                            <span className={"m-icon-CheckboxComposite checked"}></span>
                            <span  className={"migration-checkbox-blurb"}>Create an Azure account. If you already have an Azure account that you want to use, check this box and go to the next step.</span>
                        </div>
                    </label>
                </div>
                <div className={"migration-checkbox second-checkbox"}>
                    <label className={"m-checkbox-container"}>
                        <div className={"migration-checkbox-alignment"}>
                            <input className={"m-checkbox"} type="checkbox" onChange={() => this.props.onCheckSecondCheckbox()} />
                            <span className={"m-icon-Checkbox unchecked"}></span>
                            <span className={"m-icon-CheckboxComposite checked"}></span>
                            <span className={"migration-checkbox-blurb"}>Go to the bot’s Settings and add your Azure account as a co-owner to the bot you want to migrate. Then sign in with the Azure account.</span>
                        </div>
                    </label>
                </div>
                {this.renderButtons(false)}
                {this.renderlastFocusTrap()}
            </div>
        );
    }

    /** Renders the prompt for ABSv2 migration */
    private renderABSv2View(): JSX.Element {
        return (
            <div className={"migration-dialog-container"}>
                {this.renderFirstFocusTrap()}
                {this.renderCloseButton()}
                <h1 id="dialog-title" className={"migration-title"} >Migration unavailable</h1>
                <div className={"migration-text"}>Migration is currently disabled for Conversation Designer (ABSv2) bots.</div>
                <div className={"migration-button-row"}>
                    <button className={"m-button-primary-md"} ref={(ctl) => this._focusControl = ctl} onFocus={this.handleBotMigrationDialogFocus} onClick={() => this.props.onCancelClick()}>Ok</button>
                </div>
                {this.renderlastFocusTrap()}
            </div>
        );
    }

    /** Renders the post-migration confirmation prompt */
    private renderConfirmationView(): JSX.Element {
        return (
            <div className={"migration-dialog-container"}>
                {this.renderFirstFocusTrap()}
                {this.renderCloseButton()}
                <h1 id="dialog-title" className={"migration-title"}>Migration completed</h1>
                <div className={"migration-text"}>Go to your newly migrated bot to test it. Make sure to also reconfigure continuous deployment if you were previously using it. <a href="https://aka.ms/azure/bots/migration" target="_blank">Learn more</a></div>
                <div className={"migration-button-row"}>
                    <button className={"m-button-primary-md"} ref={(ctl) => this._focusControl = ctl} onFocus={this.handleBotMigrationDialogFocus} onClick={() => this.props.onOpenNewBotClick()}>Open the new bot</button>
                </div>
                {this.renderlastFocusTrap()}
            </div>
        );
    }

    /** Renders the prompt that shows error messages */
    private renderErrorMessageView(): JSX.Element {
        const link = this.state.error.link
            ? <a href={this.state.error.link.url} target={"_blank"}>{ this.state.error.link.text }</a>
            : null;
        return (
            <div className={"migration-dialog-container"}>
                {this.renderFirstFocusTrap()}
                {this.renderCloseButton()}
                <h1 id="dialog-title" className={"migration-title"}>{ this.state.error.title }</h1>
                <div className={"migration-text"}>{ this.state.error.message } {link}</div>
                <div className={"migration-button-row"}>
                    <button className={"m-button-primary-md"} onClick={() => this.props.onCancelClick()}  ref={(ctl) => this._focusControl = ctl} onFocus={this.handleBotMigrationDialogFocus}>Ok</button>
                </div>
                {this.renderlastFocusTrap()}
            </div>
        );
    }

    /** Renders the prompt that shows a loading spinner while necessary information is gathered for migration */
    private renderLoadingView(): JSX.Element {
        return (
            <div className={"migration-dialog-container"}>
                {this.renderlastFocusTrap()}
                {this.renderCloseButton()}     
                <div className={"migration-loading-status"} id="dialog-title">
                    Loading bot...
                </div>
                <div className={"migration-loading-spinner m-spinner-five"}>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                {this.renderButtons(true)}
                {this.renderlastFocusTrap()}
            </div>
        );
    }

    /** Renders the element */
    public render(): JSX.Element {

        // non-Azure account
        if (!this.state.azureAccessToken) {
            return this.renderNonAzureAccountView();
        }

        // error message
        if (this.state.error) {
            return this.renderErrorMessageView();
        }

        // still loading
        const isLoading = this.state.isRegistrationBot ? 
            !this.state.tenants || (this.state.tenants.length <= 1  && (!this.state.subscriptions.length || !this.state.locations.length))
            : (!this.state.botSettings || !this.state.bot);
        if (isLoading) {
            return this.renderLoadingView();
        }

        // ABSv2 bot
        if (this.state.isDesignerBot) {
            return this.renderABSv2View();
        }

        // confirmation dialog
        if (this.state.migrationIsComplete) {
            return this.renderConfirmationView();
        }

        // default
        return (
            <div className={"migration-dialog-container"}>
                {this.renderFirstFocusTrap()}          
                {this.renderCloseButton()}     
                <h1 id="dialog-title" className={"migration-title"}>Migrate { this.props.botId }</h1>
                {this.renderInputs()}
                {this.renderButtons(false)}
                {this.renderlastFocusTrap()}
            </div>
        );
    }

    public renderFirstFocusTrap(): JSX.Element {
        return (
            <div
                    className="focus-trap"
                    aria-hidden="true"
                    onFocus={this.handleFirstFocusTrapFocusx}
                    tabIndex={0}
                />
        );
    }

    public renderCloseButton(): JSX.Element {
        return (
            <button className="dialog-close-btn m-icon-Cancel" role="button" aria-label="Close Dialog" ref={(closeBtn) => this._closeBtn = closeBtn}  onFocus={this.handleBotMigrationDialogFocus} onClick={() => this.props.onCloseBtnClick()}></button>
        );
    }

    public renderlastFocusTrap(): JSX.Element {
        return (
            <div
            className="focus-trap"
            aria-hidden="true"
            onFocus={this.handleLastFocusTrapFocus}
            tabIndex={0}
        />
        );
    }

}



// WEBPACK FOOTER //
// ./Client/v4/Views/BotMigrationDialog.tsx